import { createContext } from 'preact';
import { useContext } from 'preact/hooks';

/**
 * Everything needed to create a checkout session, captured at the moment the visitor picks a
 * product.
 *
 * `associatedHabitatIds` matters: the single-habitat day pass and the sponsorship tiers are
 * *generic* products, and the habitat they unlock is attached per-checkout rather than baked into
 * the product. Replaying a deferred selection without it would grant access to nothing.
 */
export interface ProductSelection {
  productId: string;
  priceId: string;
  associatedHabitatIds?: string[];
  offerId?: string;
}

/**
 * How a product card should behave when it is clicked, when something other than the default is
 * needed. Absent everywhere except inside the Speedy Flow.
 *
 * - `onSelectProduct` intercepts the click *before* any request. The flow's guest path needs it:
 *   the visitor picks what they want before they have an account, and a checkout session cannot be
 *   created for someone who is not signed in yet. The selection is replayed once they are.
 * - `onCheckoutUrl` intercepts a URL that already exists, so it opens in a tab instead of
 *   navigating this page away. It is handed the selection as well as the URL: the URL is opaque, so
 *   without it the flow could not name what was bought on the confirmation card.
 *
 * A context rather than props because the cards are rendered several layers down by a shared
 * renderer, and threading options through every card would touch six components to change one
 * behaviour.
 */
export interface CheckoutInterception {
  onSelectProduct?: (selection: ProductSelection) => void;
  onCheckoutUrl?: (checkoutUrl: string, selection: ProductSelection) => void;
  /**
   * Opens a blank tab synchronously, inside the click that triggered it, so the browser does not
   * treat the later `window.open` as an unsolicited popup. See `useProductCheckout`.
   */
  reserveCheckoutTab?: () => void;
  /**
   * Stamps a card's return path so the checkout tab knows to report back to its opener rather than
   * navigate. Supplied by the flow, because only the flow knows it is running.
   */
  markReturnPath?: (path?: string) => string | undefined;
  /**
   * An upgrade resolved in place — a prorated tier change, no checkout at all. The flow has to
   * hear about it, or the modal sits on the product list after a purchase that already succeeded.
   */
  onUpgraded?: () => void;
  /**
   * The checkout session could not be created — the server refused the product, or the request
   * simply failed. The flow has to hear about it for the same reason as `onUpgraded`: it reserved
   * a tab inside the click, and only it can release that tab and move the modal off a payment that
   * is never going to start.
   */
  onCheckoutFailed?: (error: unknown) => void;
  /**
   * What they picked last time, when they have been here before — they cancelled at Stripe and came
   * back. A card that offers a choice restores it, so changing your mind costs one click rather
   * than making the whole choice again.
   */
  previousSelection?: ProductSelection;
}

const CheckoutHandlerContext = createContext<CheckoutInterception | undefined>(undefined);

export const CheckoutHandlerProvider = CheckoutHandlerContext.Provider;

export const useCheckoutInterception = () => useContext(CheckoutHandlerContext);

export default CheckoutHandlerContext;
